import React, { useState, useRef, useEffect } from "react";
import { API } from "@/context/AuthContext";
import { MessageCircle, X, Send, Loader2, Bot, User, Minimize2, Maximize2, Minimize, CheckCircle2, RotateCcw } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const RECENT_Q_KEY = "valoriza_ai_chat_recent_questions";
const MAX_RECENT = 5;

export default function AiChat() {
  const [open, setOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState(false); // Iter318 · feedback: pantalla completa para leer mejor respuestas largas
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hola, soy el asistente de Valoriza Plataformas Corporativas. Puedo responder preguntas sobre proyectos, ofertas, backlog, presupuesto y checklists — y generarte un Excel/Word/PowerPoint si lo necesitas. ¿En qué puedo ayudarte?" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [steps, setSteps] = useState([]); // trail de pasos visibles (p.ej. "Generando documento…")
  const [elapsed, setElapsed] = useState(0); // R01 · segundos transcurridos desde el envío
  const [lastFailedText, setLastFailedText] = useState(null); // R01 · "Reintentar" sin volver a escribir
  // Mejora sugerida tras R01-R05 · acceso rápido a las últimas preguntas
  // reales enviadas, para no reescribirlas cada vez.
  const [recentQuestions, setRecentQuestions] = useState(() => {
    try { return JSON.parse(localStorage.getItem(RECENT_Q_KEY) || "[]"); } catch { return []; }
  });
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null); // R01 · cancelar una consulta en curso
  const elapsedTimerRef = useRef(null);
  const [frequentQuestions, setFrequentQuestions] = useState([]);

  useEffect(() => {
    if (open && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, open, steps]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const token = localStorage.getItem("valoriza_token") || "";
    fetch(`${API}/ai/chat/frequent-questions`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.ok ? r.json() : { questions: [] })
      .then((d) => setFrequentQuestions(d.questions || []))
      .catch(() => {});
  }, [open]);

  const sendMessage = async (overrideText) => {
    // overrideText es string sólo cuando viene de "Reintentar"; onClick del
    // botón send pasa el SyntheticEvent como primer argumento.
    const override = typeof overrideText === "string" ? overrideText : null;
    const text = (override ?? input).trim();
    if (!text || loading) return;
    setLastFailedText(null);
    const userMsg = { role: "user", content: text };
    const history = messages.map(m => ({ role: m.role, content: m.content }));
    if (override) {
      setMessages(prev => [...prev, { role: "assistant", content: "" }]);
    } else {
      setMessages(prev => [...prev, userMsg, { role: "assistant", content: "" }]);
      setInput("");
      setRecentQuestions((prev) => {
        const next = [text, ...prev.filter((q) => q !== text)].slice(0, MAX_RECENT);
        try { localStorage.setItem(RECENT_Q_KEY, JSON.stringify(next)); } catch { /* noop */ }
        return next;
      });
    }
    setLoading(true);
    setSteps([]);
    setElapsed(0);

    // R01 · cronómetro visible — "lleva Xs trabajando" en vez de puntos
    // suspensivos estáticos que no distinguen "sigue vivo" de "colgado".
    const startedAt = Date.now();
    elapsedTimerRef.current = setInterval(() => {
      setElapsed(Math.round((Date.now() - startedAt) / 1000));
    }, 1000);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const token = localStorage.getItem("valoriza_token") || "";
      const resp = await fetch(`${API}/ai/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ message: text, history }),
        signal: controller.signal,
      });
      if (!resp.ok || !resp.body) throw new Error(await resp.text());

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let errored = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = JSON.parse(part.slice(6));
          if (payload.type === "token") {
            setSteps((prev) => prev.map((s) => ({ ...s, done: true })));
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = { role: "assistant", content: next[next.length - 1].content + payload.delta };
              return next;
            });
          } else if (payload.type === "status") {
            setSteps((prev) => [...prev.map((s) => ({ ...s, done: true })), { text: payload.text || "", done: false }]);
          } else if (payload.type === "error") {
            errored = true;
            setSteps([]);
            setLastFailedText(text);
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = { role: "assistant", content: payload.detail || "Lo siento, ha ocurrido un error al contactar con el asistente. Por favor, inténtalo de nuevo.", isError: true };
              return next;
            });
          }
        }
      }
      if (!errored) setSteps([]);
    } catch (e) {
      setSteps([]);
      setLastFailedText(text);
      if (e.name === "AbortError") {
        setMessages(prev => {
          const next = [...prev];
          next[next.length - 1] = { role: "assistant", content: "Consulta cancelada por el usuario.", isError: true };
          return next;
        });
      } else {
        setMessages(prev => {
          const next = [...prev];
          if (next[next.length - 1]?.role === "assistant" && !next[next.length - 1].content) {
            next[next.length - 1] = { role: "assistant", content: "Lo siento, ha ocurrido un error al contactar con el asistente. Por favor, inténtalo de nuevo.", isError: true };
            return next;
          }
          return [...prev, { role: "assistant", content: "Lo siento, ha ocurrido un error al contactar con el asistente. Por favor, inténtalo de nuevo.", isError: true }];
        });
      }
    } finally {
      clearInterval(elapsedTimerRef.current);
      abortRef.current = null;
      setLoading(false);
      setElapsed(0);
    }
  };

  const cancelMessage = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(v => !v)}
        data-testid="ai-chat-toggle"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-105 active:scale-95"
        style={{ background: "linear-gradient(135deg, #007934 0%, #00b04a 100%)" }}
        title="Asistente Pando"
      >
        {open
          ? <X size={22} className="text-white" />
          : <MessageCircle size={24} className="text-white" />
        }
      </button>

      {/* Chat panel */}
      {open && (
        <div
          data-testid="ai-chat-panel"
          className={fullscreen
            ? "fixed inset-4 z-50 bg-white rounded-2xl shadow-2xl border border-gray-100 flex flex-col overflow-hidden"
            : "fixed bottom-24 right-6 z-50 w-96 max-w-[calc(100vw-24px)] bg-white rounded-2xl shadow-2xl border border-gray-100 flex flex-col overflow-hidden"}
          style={fullscreen ? {} : { height: "480px" }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 flex-shrink-0" style={{ background: "linear-gradient(135deg, #003D1A 0%, #007934 100%)" }}>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Bot size={16} className="text-white" />
              </div>
              <div>
                <p className="text-white text-sm font-semibold leading-tight">Asistente Pando</p>
                <p className="text-green-200 text-xs">Plataformas Corporativas</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setFullscreen((v) => !v)} title={fullscreen ? "Salir de pantalla completa" : "Pantalla completa"}
                className="text-white/70 hover:text-white transition-colors p-1 rounded" data-testid="ai-chat-fullscreen-toggle">
                {fullscreen ? <Minimize size={15} /> : <Maximize2 size={15} />}
              </button>
              <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white transition-colors p-1 rounded" data-testid="ai-chat-minimize">
                <Minimize2 size={16} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50/50">
            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-2 ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${msg.role === "assistant" ? "bg-[#007934]" : "bg-gray-300"}`}>
                  {msg.role === "assistant"
                    ? <Bot size={13} className="text-white" />
                    : <User size={13} className="text-white" />
                  }
                </div>
                <div className={`${fullscreen ? "max-w-[70%]" : "max-w-[80%]"} px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                  msg.role === "assistant"
                    ? msg.isError
                      ? "bg-red-50 border border-red-200 text-red-700 rounded-tl-none shadow-sm"
                      : "bg-white border border-gray-100 text-gray-800 rounded-tl-none shadow-sm prose prose-sm prose-a:text-[#007934] prose-a:font-semibold max-w-none"
                    : "bg-[#007934] text-white rounded-tr-none"
                }`} style={{ whiteSpace: msg.role === "assistant" ? "normal" : "pre-wrap" }}>
                  {msg.content ? (
                    msg.role === "assistant" && !msg.isError ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown> : msg.content
                  ) : (loading && i === messages.length - 1 && steps.length === 0 ? (
                    <div className="flex gap-1 items-center h-5">
                      {[0, 1, 2].map(j => (
                        <div key={j} className="w-1.5 h-1.5 rounded-full bg-[#007934] animate-bounce" style={{ animationDelay: `${j * 0.15}s` }} />
                      ))}
                    </div>
                  ) : "")}
                  {msg.isError && i === messages.length - 1 && lastFailedText && (
                    <button
                      onClick={() => sendMessage(lastFailedText)}
                      data-testid="ai-chat-retry"
                      className="mt-1.5 flex items-center gap-1 text-xs font-semibold text-red-700 hover:text-red-900 underline"
                    >
                      <RotateCcw size={11} /> Reintentar
                    </button>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex gap-2 items-center pl-9" data-testid="ai-chat-elapsed">
                <span className="text-[11px] text-gray-400">
                  {elapsed < 30
                    ? `Trabajando… ${elapsed}s`
                    : elapsed < 90
                    ? `Esto está tardando más de lo habitual (${elapsed}s) — seguimos trabajando…`
                    : `Lleva ${elapsed}s — puedes cancelar y reintentar si quieres.`}
                </span>
                <button
                  onClick={cancelMessage}
                  data-testid="ai-chat-cancel"
                  className="text-[11px] font-semibold text-red-600 hover:text-red-800 underline"
                >
                  Cancelar
                </button>
              </div>
            )}
            {steps.length > 0 && (
              <div className="flex gap-2" data-testid="ai-chat-status">
                <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-[#007934]">
                  <Loader2 size={13} className="text-white animate-spin" />
                </div>
                <div className="max-w-[80%] px-3 py-2 rounded-2xl text-xs leading-relaxed bg-green-50 border border-green-100 text-green-700 rounded-tl-none space-y-1">
                  {steps.map((s, i) => (
                    <div key={i} className="flex items-center gap-1.5" data-testid={`ai-chat-step-${i}`}>
                      {s.done ? <CheckCircle2 size={11} className="text-green-500 flex-shrink-0" /> : <Loader2 size={11} className="animate-spin flex-shrink-0" />}
                      <span className={s.done ? "opacity-60" : "italic"}>{s.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="px-3 py-2.5 border-t border-gray-100 bg-white flex-shrink-0">
            {recentQuestions.length > 0 && (
              <div className="flex gap-1.5 overflow-x-auto pb-2 mb-0.5" data-testid="ai-chat-recent-questions">
                {recentQuestions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(q)}
                    disabled={loading}
                    title={q}
                    data-testid={`ai-chat-recent-q-${i}`}
                    className="flex-shrink-0 max-w-[180px] truncate text-[11px] bg-gray-100 hover:bg-[#007934]/10 text-gray-600 hover:text-[#007934] rounded-full px-2.5 py-1 border border-gray-200 disabled:opacity-40 transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
            {frequentQuestions.length > 0 && (
              <div className="flex gap-1.5 overflow-x-auto pb-2 mb-0.5" data-testid="ai-chat-frequent-questions">
                <span className="flex-shrink-0 text-[10px] text-gray-400 self-center pr-0.5">Frecuentes del equipo:</span>
                {frequentQuestions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(q.text)}
                    disabled={loading}
                    title={`${q.text} (${q.count}×)`}
                    data-testid={`ai-chat-frequent-q-${i}`}
                    className="flex-shrink-0 max-w-[180px] truncate text-[11px] bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-full px-2.5 py-1 border border-emerald-200 disabled:opacity-40 transition-colors"
                  >
                    {q.text}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-end gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus-within:border-[#007934] focus-within:bg-white transition-all">
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKey}
                placeholder="Escribe tu pregunta..."
                rows={1}
                data-testid="ai-chat-input"
                className="flex-1 text-sm bg-transparent outline-none resize-none text-gray-800 placeholder-gray-400 max-h-24"
                style={{ overflowY: "auto" }}
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                data-testid="ai-chat-send"
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all disabled:opacity-40"
                style={{ background: input.trim() && !loading ? "#007934" : "#e5e7eb" }}
              >
                {loading
                  ? <Loader2 size={14} className="animate-spin text-gray-400" />
                  : <Send size={14} className={input.trim() ? "text-white" : "text-gray-400"} />
                }
              </button>
            </div>
            <p className="text-[10px] text-gray-400 mt-1 px-1">Enter para enviar · Shift+Enter nueva línea</p>
          </div>
        </div>
      )}
    </>
  );
}
